import React, { useState, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, Plus, Trash2, Edit2, Eye, EyeOff, Save, X, 
  Type, Image, Layout, MousePointer, Columns, Minus, ArrowRight,
  Users, Calendar, Clock, ChevronDown, ChevronUp, Copy, Settings,
  Monitor, Tablet, Smartphone, FileText, Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Page Templates
const PAGE_TEMPLATES = [
  {
    id: 'blank',
    name: 'Blank Page',
    description: 'Start from scratch',
    icon: FileText,
    blocks: []
  },
  {
    id: 'landing',
    name: 'Landing Page',
    description: 'Hero, features, and CTA',
    icon: Sparkles,
    blocks: [
      {
        id: 'tpl-hero',
        type: 'hero',
        order: 0,
        visible: true,
        content: {
          title: 'Welcome to Our Page',
          subtitle: 'Add your compelling message here',
          date_text: '',
          show_countdown: false,
          cta_buttons: [
            { text: 'Get Started', path: '/signup', style: 'primary' },
            { text: 'Learn More', path: '#features', style: 'secondary' }
          ]
        },
        settings: { full_height: true, show_scroll_indicator: true }
      },
      {
        id: 'tpl-features',
        type: 'cards',
        order: 1,
        visible: true,
        content: {
          title: 'Why Choose Us',
          subtitle: 'Discover what makes us different',
          cards: [
            { icon: '🎯', title: 'Feature One', description: 'Describe your first key feature here' },
            { icon: '🚀', title: 'Feature Two', description: 'Describe your second key feature here' },
            { icon: '💡', title: 'Feature Three', description: 'Describe your third key feature here' }
          ]
        },
        settings: { columns: 3, background: 'muted' }
      },
      {
        id: 'tpl-cta',
        type: 'cta',
        order: 2,
        visible: true,
        content: {
          title: 'Ready to Get Started?',
          subtitle: 'Join us today and experience the difference',
          buttons: [{ text: 'Sign Up Now', path: '/signup', style: 'primary' }]
        },
        settings: { background: 'primary' }
      }
    ]
  },
  {
    id: 'content',
    name: 'Content Page',
    description: 'Text-focused with images',
    icon: Type,
    blocks: [
      {
        id: 'tpl-header',
        type: 'text',
        order: 0,
        visible: true,
        content: {
          heading: 'Page Title',
          body: '<p>Add your introduction paragraph here. This template is perfect for content-heavy pages like About Us, Blog posts, or informational content.</p>',
          alignment: 'center'
        },
        settings: { padding: 'large' }
      },
      {
        id: 'tpl-content1',
        type: 'text',
        order: 1,
        visible: true,
        content: {
          heading: 'First Section',
          body: '<p>Your content goes here. You can add multiple paragraphs, lists, and formatted text.</p><ul><li>Point one</li><li>Point two</li><li>Point three</li></ul>',
          alignment: 'left'
        },
        settings: { padding: 'medium' }
      },
      {
        id: 'tpl-image',
        type: 'image',
        order: 2,
        visible: true,
        content: {
          url: '',
          alt: 'Add image description',
          caption: 'Add an optional caption'
        },
        settings: { rounded: true, full_width: false }
      },
      {
        id: 'tpl-content2',
        type: 'text',
        order: 3,
        visible: true,
        content: {
          heading: 'Second Section',
          body: '<p>Continue your content here. Break up long text with images and subheadings for better readability.</p>',
          alignment: 'left'
        },
        settings: { padding: 'medium' }
      }
    ]
  },
  {
    id: 'event',
    name: 'Event Page',
    description: 'Countdown, speakers, and schedule',
    icon: Calendar,
    blocks: [
      {
        id: 'tpl-event-hero',
        type: 'hero',
        order: 0,
        visible: true,
        content: {
          title: 'Event Name',
          subtitle: 'Join us for an unforgettable experience',
          date_text: 'EVENT DATE',
          show_countdown: true,
          cta_buttons: [
            { text: 'Register Now', path: '/signup', style: 'primary' }
          ]
        },
        settings: { full_height: true, show_scroll_indicator: true }
      },
      {
        id: 'tpl-event-speakers',
        type: 'speakers_featured',
        order: 1,
        visible: true,
        content: {
          title: 'Featured Speakers',
          subtitle: 'Learn from industry leaders',
          filter_featured: true,
          max_speakers: 4
        },
        settings: { background: 'muted' }
      },
      {
        id: 'tpl-event-details',
        type: 'cards',
        order: 2,
        visible: true,
        content: {
          title: 'Event Highlights',
          subtitle: 'What to expect',
          cards: [
            { icon: '🎤', title: 'Keynote Sessions', description: 'Inspiring talks from thought leaders' },
            { icon: '🤝', title: 'Networking', description: 'Connect with peers and experts' },
            { icon: '🎓', title: 'Workshops', description: 'Hands-on learning experiences' }
          ]
        },
        settings: { columns: 3 }
      },
      {
        id: 'tpl-event-cta',
        type: 'cta',
        order: 3,
        visible: true,
        content: {
          title: 'Secure Your Spot',
          subtitle: 'Limited seats available',
          buttons: [{ text: 'Register Today', path: '/signup', style: 'primary' }]
        },
        settings: { background: 'primary' }
      }
    ]
  }
];

// Block type definitions with icons and default content
const BLOCK_TYPES = [
  { 
    type: 'hero', 
    label: 'Hero Section', 
    icon: Layout,
    description: 'Full-width hero with title, subtitle, and CTA',
    defaultContent: {
      title: 'Your Title Here',
      subtitle: 'Add your subtitle or description',
      date_text: 'EVENT DATE',
      show_countdown: true,
      cta_buttons: [{ text: 'Get Started', path: '/', style: 'primary' }]
    },
    defaultSettings: { full_height: true, show_scroll_indicator: true }
  },
  { 
    type: 'text', 
    label: 'Text Block', 
    icon: Type,
    description: 'Heading and body text',
    defaultContent: { heading: 'Section Title', body: '<p>Your content here...</p>', alignment: 'left' },
    defaultSettings: { padding: 'medium' }
  },
  { 
    type: 'image', 
    label: 'Image', 
    icon: Image,
    description: 'Single image with optional caption',
    defaultContent: { url: '', alt: '', caption: '' },
    defaultSettings: { rounded: true, full_width: false }
  },
  { 
    label: 'Cards Grid', 
    icon: Columns,
    description: 'Grid of feature cards',
    defaultContent: {
      title: 'Features',
      subtitle: 'What we offer',
      cards: [
        { icon: '🎯', title: 'Feature 1', description: 'Description here' },
        { icon: '🚀', title: 'Feature 2', description: 'Description here' },
        { icon: '💡', title: 'Feature 3', description: 'Description here' }
      ]
    },
    defaultSettings: { columns: 3, background: 'default' }
  },
  { 
    type: 'cta', 
    label: 'Call to Action', 
    icon: MousePointer,
    description: 'CTA section with buttons',
    defaultContent: {
      title: 'Ready to Get Started?',
      subtitle: 'Join us today',
      buttons: [{ text: 'Sign Up', path: '/signup', style: 'primary' }]
    },
    defaultSettings: { background: 'primary' }
  },
  { 
    type: 'spacer', 
    label: 'Spacer', 
    icon: Minus,
    description: 'Add vertical space',
    defaultContent: {},
    defaultSettings: { height: 'medium' }
  },
  { 
    type: 'speakers_featured', 
    label: 'Featured Speakers', 
    icon: Users,
    description: 'Display featured speakers',
    defaultContent: { title: 'Featured Speakers', subtitle: '', filter_featured: true, max_speakers: 6 },
    defaultSettings: { background: 'muted' }
  },
  { 
    type: 'countdown', 
    label: 'Countdown Timer', 
    icon: Clock,
    description: 'Event countdown',
    defaultContent: { title: 'Event Starts In' },
    defaultSettings: {}
  },
  {
    type: 'stats',
    label: 'Statistics',
    icon: Columns,
    description: 'Configurable stats display',
    defaultContent: {
      title: 'Key Numbers',
      subtitle: '',
      stats: [
        { value: '100+', label: 'Attendees' },
        { value: '12', label: 'Speakers' },
        { value: '1', label: 'Day' }
      ]
    },
    defaultSettings: { columns: 3, background: 'default' }
  },
  {
    type: 'speaker_grid',
    label: 'Speaker Grid',
    icon: Users,
    description: 'Live speaker grid from CMS',
    defaultContent: {
      title: 'All Speakers',
      show_search: true,
      show_filters: true,
      show_keynote_section: true,
      keynote_title: 'Hosts',
      grid_title: 'Featured',
      show_pdf_download: false
    },
    defaultSettings: { columns: 3, background: 'default' }
  },
  {
    type: 'agenda_schedule',
    label: 'Agenda Schedule',
    icon: Calendar,
    description: 'Live agenda from CMS',
    defaultContent: {
      title: 'Full Schedule',
      show_legend: true,
      show_calendar_download: true,
      show_pdf_download: false
    },
    defaultSettings: { background: 'default' }
  },
  {
    type: 'packages_grid',
    label: 'Packages Grid',
    icon: Layout,
    description: 'Sponsorship packages from CMS',
    defaultContent: {
      title: 'Sponsorship Packages',
      subtitle: 'Choose the tier that fits your organization',
      section_label: 'Partnership Tiers'
    },
    defaultSettings: { background: 'default' }
  },
  {
    type: 'contact_form',
    label: 'Contact Form',
    icon: Settings,
    description: 'Contact/inquiry form',
    defaultContent: {
      title: 'Get in Touch',
      subtitle: 'We would love to hear from you.',
      section_label: 'Contact Us',
      email_to: 'contact@globalintech.ai',
      email_subject: 'Inquiry - The Jock Tax Summit',
      button_text: 'Send Inquiry'
    },
    defaultSettings: { background: 'default' }
  }
];

// Sortable Block Item Component
const SortableBlockItem = ({ block, onEdit, onDelete, onToggleVisibility, onDuplicate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const blockType = BLOCK_TYPES.find(b => b.type === block.type);
  const IconComponent = blockType?.icon || Type;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`glass-card rounded-xl p-4 mb-3 ${!block.visible ? 'opacity-50' : ''} ${isDragging ? 'ring-2 ring-primary' : ''}`}
    >
      <div className="flex items-center gap-3">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-2 hover:bg-white/10 rounded-lg transition-colors"
        >
          <GripVertical className="w-5 h-5 text-muted-foreground" />
        </div>

        {/* Block Icon */}
        <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
          <IconComponent className="w-5 h-5 text-primary" />
        </div>

        {/* Block Info */}
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{blockType?.label || block.type}</div>
          <div className="text-sm text-muted-foreground truncate">
            {block.content?.title || block.content?.heading || blockType?.description || 'No content'}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => onToggleVisibility(block.id)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title={block.visible ? 'Hide block' : 'Show block'}
          >
            {block.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
          </button>
          <button
            onClick={() => onDuplicate(block)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Duplicate block"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={() => onEdit(block)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Edit block"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(block.id)}
            className="p-2 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
            title="Delete block"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Block Editor Modal
const BlockEditorModal = ({ block, onSave, onClose, blockType }) => {
  const [content, setContent] = useState(block?.content || {});
  const [settings, setSettings] = useState(block?.settings || {});

  const handleSave = () => {
    onSave({ ...block, content, settings });
  };

  const updateContent = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
  };

  const updateSettings = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  // Render different editors based on block type
  const renderEditor = () => {
    switch (block?.type) {
      case 'hero':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                value={content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Subtitle</label>
              <textarea
                value={content.subtitle || ''}
                onChange={(e) => updateContent('subtitle', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Date Label</label>
              <input
                type="text"
                value={content.date_text || ''}
                onChange={(e) => updateContent('date_text', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={content.show_countdown !== false}
                  onChange={(e) => updateContent('show_countdown', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Show Countdown</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.full_height !== false}
                  onChange={(e) => updateSettings('full_height', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Full Height</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">CTA Buttons</label>
              {(content.cta_buttons || []).map((btn, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={btn.text}
                    onChange={(e) => {
                      const buttons = [...(content.cta_buttons || [])];
                      buttons[idx] = { ...buttons[idx], text: e.target.value };
                      updateContent('cta_buttons', buttons);
                    }}
                    placeholder="Button text"
                    className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm"
                  />
                  <input
                    type="text"
                    value={btn.path}
                    onChange={(e) => {
                      const buttons = [...(content.cta_buttons || [])];
                      buttons[idx] = { ...buttons[idx], path: e.target.value };
                      updateContent('cta_buttons', buttons);
                    }}
                    placeholder="/path"
                    className="w-32 px-3 py-2 rounded-lg bg-background border border-border text-sm"
                  />
                  <select
                    value={btn.style}
                    onChange={(e) => {
                      const buttons = [...(content.cta_buttons || [])];
                      buttons[idx] = { ...buttons[idx], style: e.target.value };
                      updateContent('cta_buttons', buttons);
                    }}
                    className="px-3 py-2 rounded-lg bg-background border border-border text-sm"
                  >
                    <option value="primary">Primary</option>
                    <option value="secondary">Secondary</option>
                  </select>
                  <button
                    onClick={() => {
                      const buttons = (content.cta_buttons || []).filter((_, i) => i !== idx);
                      updateContent('cta_buttons', buttons);
                    }}
                    className="p-2 text-red-400 hover:bg-red-500/10 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const buttons = [...(content.cta_buttons || []), { text: 'New Button', path: '/', style: 'secondary' }];
                  updateContent('cta_buttons', buttons);
                }}
                className="text-sm text-primary hover:underline"
              >
                + Add Button
              </button>
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Heading</label>
              <input
                type="text"
                value={content.heading || ''}
                onChange={(e) => updateContent('heading', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Body (HTML supported)</label>
              <textarea
                value={content.body || ''}
                onChange={(e) => updateContent('body', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none font-mono text-sm"
                rows={6}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Alignment</label>
              <select
                value={content.alignment || 'left'}
                onChange={(e) => updateContent('alignment', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </div>
          </div>
        );

      case 'cta':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                value={content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Subtitle</label>
              <input
                type="text"
                value={content.subtitle || ''}
                onChange={(e) => updateContent('subtitle', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Background</label>
              <select
                value={settings.background || 'default'}
                onChange={(e) => updateSettings('background', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
              >
                <option value="default">Default</option>
                <option value="primary">Primary Color</option>
                <option value="muted">Muted</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Buttons</label>
              {(content.buttons || []).map((btn, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={btn.text}
                    onChange={(e) => {
                      const buttons = [...(content.buttons || [])];
                      buttons[idx] = { ...buttons[idx], text: e.target.value };
                      updateContent('buttons', buttons);
                    }}
                    placeholder="Button text"
                    className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm"
                  />
                  <input
                    type="text"
                    value={btn.path}
                    onChange={(e) => {
                      const buttons = [...(content.buttons || [])];
                      buttons[idx] = { ...buttons[idx], path: e.target.value };
                      updateContent('buttons', buttons);
                    }}
                    placeholder="/path"
                    className="w-32 px-3 py-2 rounded-lg bg-background border border-border text-sm"
                  />
                  <button
                    onClick={() => {
                      const buttons = (content.buttons || []).filter((_, i) => i !== idx);
                      updateContent('buttons', buttons);
                    }}
                    className="p-2 text-red-400 hover:bg-red-500/10 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const buttons = [...(content.buttons || []), { text: 'New Button', path: '/', style: 'primary' }];
                  updateContent('buttons', buttons);
                }}
                className="text-sm text-primary hover:underline"
              >
                + Add Button
              </button>
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Image URL</label>
              <input
                type="text"
                value={content.url || ''}
                onChange={(e) => updateContent('url', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Alt Text</label>
              <input
                type="text"
                value={content.alt || ''}
                onChange={(e) => updateContent('alt', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Caption</label>
              <input
                type="text"
                value={content.caption || ''}
                onChange={(e) => updateContent('caption', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.rounded !== false}
                  onChange={(e) => updateSettings('rounded', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Rounded Corners</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.full_width === true}
                  onChange={(e) => updateSettings('full_width', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm">Full Width</span>
              </label>
            </div>
          </div>
        );

      case 'cards':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Section Title</label>
              <input
                type="text"
                value={content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Subtitle</label>
              <input
                type="text"
                value={content.subtitle || ''}
                onChange={(e) => updateContent('subtitle', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Columns</label>
              <select
                value={settings.columns || 3}
                onChange={(e) => updateSettings('columns', parseInt(e.target.value))}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
              >
                <option value={2}>2 Columns</option>
                <option value={3}>3 Columns</option>
                <option value={4}>4 Columns</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Cards</label>
              {(content.cards || []).map((card, idx) => (
                <div key={idx} className="p-3 bg-muted/30 rounded-xl mb-2">
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={card.icon || ''}
                      onChange={(e) => {
                        const cards = [...(content.cards || [])];
                        cards[idx] = { ...cards[idx], icon: e.target.value };
                        updateContent('cards', cards);
                      }}
                      placeholder="Icon (emoji)"
                      className="w-20 px-3 py-2 rounded-lg bg-background border border-border text-sm"
                    />
                    <input
                      type="text"
                      value={card.title || ''}
                      onChange={(e) => {
                        const cards = [...(content.cards || [])];
                        cards[idx] = { ...cards[idx], title: e.target.value };
                        updateContent('cards', cards);
                      }}
                      placeholder="Card title"
                      className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm"
                    />
                    <button
                      onClick={() => {
                        const cards = (content.cards || []).filter((_, i) => i !== idx);
                        updateContent('cards', cards);
                      }}
                      className="p-2 text-red-400 hover:bg-red-500/10 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <textarea
                    value={card.description || ''}
                    onChange={(e) => {
                      const cards = [...(content.cards || [])];
                      cards[idx] = { ...cards[idx], description: e.target.value };
                      updateContent('cards', cards);
                    }}
                    placeholder="Card description"
                    className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm"
                    rows={2}
                  />
                </div>
              ))}
              <button
                onClick={() => {
                  const cards = [...(content.cards || []), { icon: '✨', title: 'New Card', description: '' }];
                  updateContent('cards', cards);
                }}
                className="text-sm text-primary hover:underline"
              >
                + Add Card
              </button>
            </div>
          </div>
        );

      case 'spacer':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Height</label>
              <select
                value={settings.height || 'medium'}
                onChange={(e) => updateSettings('height', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
              >
                <option value="small">Small (32px)</option>
                <option value="medium">Medium (64px)</option>
                <option value="large">Large (128px)</option>
              </select>
            </div>
          </div>
        );

      case 'speakers_featured':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Section Title</label>
              <input
                type="text"
                value={content.title || ''}
                onChange={(e) => updateContent('title', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Subtitle</label>
              <input
                type="text"
                value={content.subtitle || ''}
                onChange={(e) => updateContent('subtitle', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Max Speakers to Show</label>
              <input
                type="number"
                value={content.max_speakers || 6}
                onChange={(e) => updateContent('max_speakers', parseInt(e.target.value))}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none"
                min={1}
                max={20}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={content.filter_featured !== false}
                onChange={(e) => updateContent('filter_featured', e.target.checked)}
                className="rounded"
              />
              <span className="text-sm">Show only featured speakers</span>
            </div>
          </div>
        );

      case 'stats':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input type="text" value={content.title || ''} onChange={(e) => updateContent('title', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Subtitle</label>
              <input type="text" value={content.subtitle || ''} onChange={(e) => updateContent('subtitle', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Columns</label>
              <select value={settings.columns || 4} onChange={(e) => updateSettings('columns', parseInt(e.target.value))}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none">
                <option value={2}>2</option><option value={3}>3</option><option value={4}>4</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Stats</label>
              {(content.stats || []).map((stat, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <input type="text" value={stat.value || ''} placeholder="Value" onChange={(e) => {
                    const stats = [...(content.stats || [])]; stats[idx] = { ...stats[idx], value: e.target.value }; updateContent('stats', stats);
                  }} className="w-24 px-3 py-2 rounded-lg bg-background border border-border text-sm" />
                  <input type="text" value={stat.label || ''} placeholder="Label" onChange={(e) => {
                    const stats = [...(content.stats || [])]; stats[idx] = { ...stats[idx], label: e.target.value }; updateContent('stats', stats);
                  }} className="flex-1 px-3 py-2 rounded-lg bg-background border border-border text-sm" />
                  <button onClick={() => updateContent('stats', (content.stats || []).filter((_, i) => i !== idx))} className="p-2 text-red-400 hover:bg-red-500/10 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button onClick={() => updateContent('stats', [...(content.stats || []), { value: '0', label: 'New Stat' }])} className="text-sm text-primary hover:underline">+ Add Stat</button>
            </div>
          </div>
        );

      case 'speaker_grid':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Keynote Section Title</label>
              <input type="text" value={content.keynote_title || ''} onChange={(e) => updateContent('keynote_title', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Grid Section Title</label>
              <input type="text" value={content.grid_title || ''} onChange={(e) => updateContent('grid_title', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Grid Columns</label>
              <select value={settings.columns || 3} onChange={(e) => updateSettings('columns', parseInt(e.target.value))}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none">
                <option value={2}>2</option><option value={3}>3</option><option value={4}>4</option>
              </select>
            </div>
            <div className="space-y-2">
              {[['show_search', 'Show Search Bar'], ['show_filters', 'Show Tag Filters'], ['show_keynote_section', 'Show Keynote Section'], ['show_pdf_download', 'Show PDF Download']].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2">
                  <input type="checkbox" checked={content[key] !== false} onChange={(e) => updateContent(key, e.target.checked)} className="rounded" />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'agenda_schedule':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input type="text" value={content.title || ''} onChange={(e) => updateContent('title', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" />
            </div>
            <div className="space-y-2">
              {[['show_legend', 'Show Session Type Legend'], ['show_calendar_download', 'Show Calendar Download'], ['show_pdf_download', 'Show PDF Download']].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2">
                  <input type="checkbox" checked={content[key] !== false} onChange={(e) => updateContent(key, e.target.checked)} className="rounded" />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">Schedule data comes from the CMS Agenda tab. Edit schedule items there.</p>
          </div>
        );

      case 'packages_grid':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Section Label</label>
              <input type="text" value={content.section_label || ''} onChange={(e) => updateContent('section_label', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input type="text" value={content.title || ''} onChange={(e) => updateContent('title', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Subtitle</label>
              <input type="text" value={content.subtitle || ''} onChange={(e) => updateContent('subtitle', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" />
            </div>
            <p className="text-sm text-muted-foreground">Package data comes from the CMS Packages tab. Edit packages there.</p>
          </div>
        );

      case 'contact_form':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Section Label</label>
              <input type="text" value={content.section_label || ''} onChange={(e) => updateContent('section_label', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input type="text" value={content.title || ''} onChange={(e) => updateContent('title', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Subtitle</label>
              <input type="text" value={content.subtitle || ''} onChange={(e) => updateContent('subtitle', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Recipient Email</label>
              <input type="email" value={content.email_to || ''} onChange={(e) => updateContent('email_to', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email Subject</label>
              <input type="text" value={content.email_subject || ''} onChange={(e) => updateContent('email_subject', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Button Text</label>
              <input type="text" value={content.button_text || ''} onChange={(e) => updateContent('button_text', e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none" />
            </div>
          </div>
        );

      default:
        return (
          <div className="text-muted-foreground">
            No editor available for this block type. Raw content:
            <pre className="mt-2 p-4 bg-muted/30 rounded-xl text-xs overflow-auto">
              {JSON.stringify(content, null, 2)}
            </pre>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="glass-card rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold">Edit {blockType?.label || 'Block'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {renderEditor()}

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors"
          >
            <Save className="w-4 h-4" />
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Page Builder Component
const PageBuilder = ({ pageSlug, credentials, onSave }) => {
  const [blocks, setBlocks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop, tablet, mobile

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const getAuthHeader = useCallback(() => {
    if (!credentials) return {};
    return { 'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`) };
  }, [credentials]);

  // Load page blocks
  React.useEffect(() => {
    const loadPage = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/pages/${pageSlug}`);
        if (response.ok) {
          const data = await response.json();
          setBlocks(data.blocks || []);
        }
      } catch (err) {
        console.error('Failed to load page:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadPage();
  }, [pageSlug]);

  // Save changes
  const saveChanges = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/pages/${pageSlug}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ blocks })
      });
      if (response.ok) {
        setHasChanges(false);
        if (onSave) onSave();
      }
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Apply template
  const applyTemplate = (template) => {
    if (blocks.length > 0 && !window.confirm('This will replace all existing blocks. Continue?')) {
      return;
    }
    // Generate unique IDs for template blocks
    const newBlocks = template.blocks.map((block, idx) => ({
      ...block,
      id: `${block.id}-${Date.now()}-${idx}`,
      order: idx
    }));
    setBlocks(newBlocks);
    setShowTemplates(false);
    setHasChanges(true);
  };

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setBlocks((items) => {
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        // Update order values
        return newItems.map((item, idx) => ({ ...item, order: idx }));
      });
      setHasChanges(true);
    }
  };

  // Add new block
  const addBlock = (blockType) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type: blockType.type,
      order: blocks.length,
      visible: true,
      content: { ...blockType.defaultContent },
      settings: { ...blockType.defaultSettings }
    };
    setBlocks([...blocks, newBlock]);
    setShowAddBlock(false);
    setHasChanges(true);
  };

  // Edit block
  const handleEditBlock = (block) => {
    setEditingBlock(block);
  };

  // Save edited block
  const handleSaveBlock = (updatedBlock) => {
    setBlocks(blocks.map(b => b.id === updatedBlock.id ? updatedBlock : b));
    setEditingBlock(null);
    setHasChanges(true);
  };

  // Delete block
  const handleDeleteBlock = (blockId) => {
    if (window.confirm('Are you sure you want to delete this block?')) {
      setBlocks(blocks.filter(b => b.id !== blockId));
      setHasChanges(true);
    }
  };

  // Toggle visibility
  const handleToggleVisibility = (blockId) => {
    setBlocks(blocks.map(b => b.id === blockId ? { ...b, visible: !b.visible } : b));
    setHasChanges(true);
  };

  // Duplicate block
  const handleDuplicateBlock = (block) => {
    const newBlock = {
      ...block,
      id: `block-${Date.now()}`,
      order: block.order + 0.5
    };
    const newBlocks = [...blocks, newBlock]
      .sort((a, b) => a.order - b.order)
      .map((b, idx) => ({ ...b, order: idx }));
    setBlocks(newBlocks);
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  // Get preview iframe width based on mode
  const getPreviewWidth = () => {
    switch (previewMode) {
      case 'mobile': return '375px';
      case 'tablet': return '768px';
      default: return '100%';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h3 className="text-xl font-semibold">Page Builder: {pageSlug}</h3>
          <p className="text-sm text-muted-foreground">Drag blocks to reorder. Click to edit.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Templates Button */}
          <button
            onClick={() => setShowTemplates(true)}
            className="flex items-center gap-2 px-4 py-2 glass-card rounded-xl text-sm font-medium hover:bg-white/10 transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            Templates
          </button>
          
          {/* Responsive Preview Buttons */}
          <div className="flex items-center glass-card rounded-xl p-1">
            <button
              onClick={() => setPreviewMode('desktop')}
              className={`p-2 rounded-lg transition-colors ${previewMode === 'desktop' ? 'bg-primary text-primary-foreground' : 'hover:bg-white/10'}`}
              title="Desktop view"
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPreviewMode('tablet')}
              className={`p-2 rounded-lg transition-colors ${previewMode === 'tablet' ? 'bg-primary text-primary-foreground' : 'hover:bg-white/10'}`}
              title="Tablet view"
            >
              <Tablet className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPreviewMode('mobile')}
              className={`p-2 rounded-lg transition-colors ${previewMode === 'mobile' ? 'bg-primary text-primary-foreground' : 'hover:bg-white/10'}`}
              title="Mobile view"
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>

          {hasChanges && (
            <span className="text-sm text-amber-400">Unsaved changes</span>
          )}
          <button
            onClick={saveChanges}
            disabled={!hasChanges || isSaving}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors ${
              hasChanges 
                ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
          >
            {isSaving ? (
              <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Page
          </button>
        </div>
      </div>

      {/* Responsive Preview Info */}
      {previewMode !== 'desktop' && (
        <div className="glass-card rounded-xl p-3 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Preview mode: <span className="font-medium text-foreground">{previewMode === 'tablet' ? 'Tablet (768px)' : 'Mobile (375px)'}</span>
          </span>
          <button
            onClick={() => setPreviewMode('desktop')}
            className="text-sm text-primary hover:underline"
          >
            Exit preview
          </button>
        </div>
      )}

      {/* Blocks List */}
      <div 
        className="glass-card rounded-xl p-4 transition-all duration-300"
        style={{ maxWidth: getPreviewWidth(), margin: previewMode !== 'desktop' ? '0 auto' : undefined }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={blocks.map(b => b.id)}
            strategy={verticalListSortingStrategy}
          >
            {blocks.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Layout className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="mb-4">No blocks yet. Start with a template or add blocks manually.</p>
                <button
                  onClick={() => setShowTemplates(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:bg-primary/90"
                >
                  <Sparkles className="w-4 h-4" />
                  Choose a Template
                </button>
              </div>
            ) : (
              blocks
                .sort((a, b) => a.order - b.order)
                .map((block) => (
                  <SortableBlockItem
                    key={block.id}
                    block={block}
                    onEdit={handleEditBlock}
                    onDelete={handleDeleteBlock}
                    onToggleVisibility={handleToggleVisibility}
                    onDuplicate={handleDuplicateBlock}
                  />
                ))
            )}
          </SortableContext>
        </DndContext>

        {/* Add Block Button */}
        <button
          onClick={() => setShowAddBlock(true)}
          className="w-full py-4 border-2 border-dashed border-border hover:border-primary rounded-xl flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Block
        </button>
      </div>

      {/* Templates Modal */}
      {showTemplates && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="glass-card rounded-2xl p-6 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold">Page Templates</h3>
                <p className="text-sm text-muted-foreground mt-1">Choose a template to get started quickly</p>
              </div>
              <button onClick={() => setShowTemplates(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {PAGE_TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  onClick={() => applyTemplate(template)}
                  className="p-6 glass-card rounded-xl hover:bg-white/10 transition-colors text-left group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <template.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold text-lg">{template.name}</div>
                      <div className="text-sm text-muted-foreground mt-1">{template.description}</div>
                      <div className="text-xs text-primary mt-2">
                        {template.blocks.length === 0 ? 'Empty canvas' : `${template.blocks.length} blocks included`}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            {blocks.length > 0 && (
              <p className="text-sm text-amber-400 mt-4 text-center">
                ⚠️ Applying a template will replace your current blocks
              </p>
            )}
          </div>
        </div>
      )}

      {/* Add Block Modal */}
      {showAddBlock && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="glass-card rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold">Add Block</h3>
              <button onClick={() => setShowAddBlock(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {BLOCK_TYPES.map((blockType) => (
                <button
                  key={blockType.type}
                  onClick={() => addBlock(blockType)}
                  className="p-4 glass-card rounded-xl hover:bg-white/10 transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center mb-3">
                    <blockType.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="font-medium">{blockType.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">{blockType.description}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Edit Block Modal */}
      {editingBlock && (
        <BlockEditorModal
          block={editingBlock}
          blockType={BLOCK_TYPES.find(b => b.type === editingBlock.type)}
          onSave={handleSaveBlock}
          onClose={() => setEditingBlock(null)}
        />
      )}
    </div>
  );
};

export default PageBuilder;
